import { Card, CardContent, Text } from '@cretesuite/design-system'
import {
  IonContent,
  IonPage,
  IonRefresher,
  IonRefresherContent,
} from '@ionic/react'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useClient, useQuery } from 'urql'
import { GreetingHeader } from '../components/GreetingHeader'
import { AssignmentCardSlim } from '../components/assignments/AssignmentCardSlim'
import { GetOperatorWithAssignments } from '../graphql/assignments'
import { NETWORK_ONLY } from '../helpers/requestPolicy'
import { useCurrentUser } from '../hooks/useCurrentUser'
import { useFeature } from '../hooks/useFeatures'
import { useIonicRefresher } from '../hooks/useIonicRefresher'
import { useNetwork } from '../hooks/useNetwork'
import { usePagePolling } from '../hooks/usePagePolling'
import { usePrefetchAssignmentData } from '../hooks/usePrefetchAssignmentData'

// Short enough that a new assignment shows up while the crew sits on this
// screen (QA), long enough for the cell radio to idle between polls; still
// no-ops while hidden or offline.
const POLL_INTERVAL_MS = 30 * 1000

export const AssignmentsPage = () => {
  const { _ } = useLingui()
  const { user } = useCurrentUser()
  const reportAtDuration = useFeature('assignmentReportAtDuration')

  const client = useClient()
  // Shared with `refetch`: same variables → same urql operation key
  const variables = useMemo(() => ({ operatorId: user?.id ?? -1 }), [user?.id])
  const [result] = useQuery({
    query: GetOperatorWithAssignments,
    pause: !user,
    variables,
    // Show the cache instantly, then revalidate from the network in the
    // background so the list updates on its own (new assignments, status
    // changes) without a manual pull-to-refresh. Offline-safe: the cache still
    // serves and the error is suppressed below when there's no connection.
    requestPolicy: 'cache-and-network',
  })

  const assignments = result.data?.operators?.[0]?.assignments ?? []
  const loading = !result.data && result.fetching

  const { isOnline } = useNetwork()
  // Warm the cache with each assignment's screens (detail, orders, work tickets,
  // signatures) so the full flow works offline.
  usePrefetchAssignmentData(assignments, isOnline)

  const refetch = useCallback(() => {
    // Mirror the query's `pause`
    if (!user) return Promise.resolve()
    return client
      .query(GetOperatorWithAssignments, variables, NETWORK_ONLY)
      .toPromise()
  }, [client, user, variables])

  const { onRefresh } = useIonicRefresher(refetch)

  // Freshness triggers (v1 parity): poll, tab re-enter, resume, reconnect
  usePagePolling({
    refresh: refetch,
    pagePath: '/assignments',
    intervalMs: POLL_INTERVAL_MS,
  })

  return (
    <IonPage>
      <GreetingHeader
        name={user?.firstName ?? undefined}
        subtitle={_(msg`Here are your assignments`)}
      />
      <IonContent>
        <IonRefresher slot='fixed' onIonRefresh={onRefresh}>
          <IonRefresherContent />
        </IonRefresher>

        <div className='ion-padding'>
          {/* Only surface a query error when there's nothing cached to show and
              we're actually online — never block the user's cached assignments
              (or flash a scary error) just because the connection dropped. */}
          {result.error && !result.data && isOnline && (
            <Card className='assignment-card--cancelled'>
              <CardContent>
                <Text variant='body'>{result.error.message}</Text>
              </CardContent>
            </Card>
          )}

          {loading && (
            <div className='text-center ion-padding'>
              <Text variant='body' className='text-muted-foreground'>
                {_(msg`Loading assignments...`)}
              </Text>
            </div>
          )}

          {assignments.map((assignment, index) => (
            <Link
              to={`/assignments/${assignment.id}`}
              key={assignment.id}
              className='no-style'
            >
              <AssignmentCardSlim
                assignment={assignment}
                index={index}
                reportAtDurationSeconds={reportAtDuration}
              />
            </Link>
          ))}

          {!loading && assignments.length === 0 && (
            <Card>
              <CardContent className='text-center'>
                <Text variant='body' className='text-muted-foreground'>
                  {_(msg`You have no assignments at this time.`)}
                </Text>
              </CardContent>
            </Card>
          )}
        </div>
      </IonContent>
    </IonPage>
  )
}

export default AssignmentsPage
